import { useEnvironmentVariables } from "#app/EnvironmentProvider.tsx";
import { useSharedTranslation } from "#hooks/useSharedTranslation.ts";
import { useTracking } from "#hooks/useTracking.ts";
import { MixpanelEvent } from "#types/tracking.ts";
import { Button, Flex, useColorSchemeValue, type BorderValue, type ColorValue } from "@hopper-ui/components";
import { WarningIcon } from "@hopper-ui/icons";
import { useCallback, type RefObject } from "react";

interface Props {
    ref: RefObject<HTMLDivElement | null> | null;
}

export const AssistantMessageUnauthenticated = ({ ref }: Props) => {
    const { tShared } = useSharedTranslation();
    const { trackEvent } = useTracking();
    const { loginPageUrl } = useEnvironmentVariables();

    const handleLogin = useCallback(() => {
        trackEvent(MixpanelEvent.ButtonClicked, {
            Location: "Assistant Conversation Answer Error",
            Trigger: "Login"
        });

        const returnUrl = new URLSearchParams({ returnUrl: window.location.href });
        window.location.href = `${loginPageUrl}?${returnUrl}`;
    }, [loginPageUrl, trackEvent]);

    const textColor = useColorSchemeValue(
        "danger",
        "warning-text-weak"
    ) as ColorValue;

    const buttonTextColor = useColorSchemeValue(
        "core_orchid-bloom-700",
        "core_samoyed"
    ) as ColorValue;

    const buttonBorderColor = useColorSchemeValue(
        "core_orchid-bloom-400",
        "core_samoyed"
    ) as BorderValue;

    return (
        <Flex
            ref={ref}
            direction="column"
            gap="inline-md"
        >
            <Flex
                direction="column"
                gap="inline-md"
                backgroundColor="danger-weak"
                padding="inset-md"
                borderRadius="pill"
                wordBreak="break-word"
            >
                <Flex
                    alignItems="center"
                    justifyContent="space-between"
                    gap="inline-md"
                    fontSize="body-sm"
                    color={textColor}
                    wrap="wrap"
                >
                    <Flex
                        alignItems="center"
                        gap="inline-sm"
                    >
                        <WarningIcon
                            flexShrink={0}
                            fill="currentcolor"
                            size="sm"
                        />
                        {tShared("errors.unauthenticated")}
                    </Flex>
                    <Flex gap="inline-sm">
                        <Button
                            variant="secondary"
                            size="sm"
                            border={buttonBorderColor}
                            color={buttonTextColor}
                            onPress={handleLogin}
                        >
                            {tShared("login")}
                        </Button>
                    </Flex>
                </Flex>
            </Flex>
        </Flex>
    );
};
